<template>
	<header v-sticky="{
		priority: 0,
		shrinkClass: 'shrink',
		verticalShrink: true,
		shrinkAmount: 1
	}">
		<div class="flex">
			<div style="position: relative">
				<FilteredSelect :options="filteredProjects" :selected="selectedProject" :showIcons="true"
					:iconMap="projectTypeIconMap" width="280px" :showTags="true" :getTagForItem="getProjectTag"
					@changeItem="changeProject" data-cy="tm_select_project" />
				<div style="position: absolute; top: -1.5rem; left: 0" v-if="super_admin.show_market_id">
					{{ selectedProject }}
				</div>
			</div>

			<div style="position: relative" v-if="!isProjectPaused">
				<FilteredSelect :options="filteredMarkets" :selected="selectedMarket" min-width="120px"
					max-width="150px" @changeItem="changeMarket" :getTagForItem="getProjectTag" :showTags="true"
					:compactTag="true" data-cy="tm_select_market" />
				<div style="position: absolute; top: -1.5rem; left: 0" v-if="super_admin.show_market_id">
					{{ selectedMarket }}
				</div>
			</div>

			<featureSection :feature-name="featureName" :show="false" style="margin: 0">
				<FilteredSelect
					v-if="!isProjectPaused && !isMarketPaused && ['question-details', 'keyword-details', 'assigned-labels', 'generated-labels', 'visibility', 'dashboard', 'team-management', 'seo-analysis', 'geo-analysis', 'finder'].includes(route.name)"
					:options="visibilityOptions" :selected="selectedVisibility" width="140px"
					@changeItem="(id) => selectedVisibility = id" data-cy="tm_select_visibility" />
			</featureSection>

			<featureSection :feature-name="route.name === 'visibility' ? 'people also ask' : 'dashboard geo'"
				:show="false" style="margin: 0" v-if="selectedVisibility == 'ai'">
				<FilteredSelect
					v-if="!isProjectPaused && !isMarketPaused && ['dashboard', 'team-management', 'geo-analysis', 'visibility'].includes(route.name)"
					:options="route.name === 'visibility' ? aiTypeOptionsVisibilityFiltered : aiTypeOptionsDashboardFiltered"
					:selected="route.name === 'visibility' ? selectedAiTypeVisibility : selectedAiTypeDashboard"
					width="170px" :showIcons="true"
					:iconMap="route.name === 'visibility' ? aiTypeIconMapVisibility : aiTypeIconMapDashboard"
					@changeItem="(id) => route.name === 'visibility' ? selectedAiTypeVisibility = id : selectedAiTypeDashboard = id"
					data-cy="tm_select_ai_type" />
			</featureSection>
		</div>
		<Profile />
	</header>
</template>

<script setup>
import { onMounted, ref, watch, computed } from 'vue';
import { jam } from '@jam.dev/sdk';
import FilteredSelect from '../forms/FilteredSelect.vue';
import Profile from '../profile/profile.vue';
import featureSection from '@/components/utilities/featureSection.vue';
import chatgptIcon from '@/assets/icon/chatgpt-icon.svg';
import aiOverviewIcon from '@/assets/icon/google-logo.svg';
import robotIcon from '@/assets/icon/robot.svg';
import perplexityIcon from '@/assets/icon/perplexity-icon.svg';
import { useMarketStore } from '@/store/global/market';
import { useAuthStore } from '@/store/global/auth';
import { useProfileStore } from '@/store/global/profile.js';
import { useKeywordsStore } from '@/store/visibility/keywords';
import { useSubNavigationStore } from '@/store/global/subNavigation';
import { useAiBrandMonitorStore } from '@/store/visibility/aiBrandMonitor';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';

const marketStore = useMarketStore();
const { projects, selectedProject, markets, selectedMarket, force_key, redirectRoute, perplexity_enabled } = storeToRefs(marketStore);

function getItemById(items, id) {
	return items.find(item => item.id === id) || null;
}

const selectedProjectItem = computed(() => getItemById(projects.value, selectedProject.value));
const selectedMarketItem = computed(() => getItemById(markets.value, selectedMarket.value));
const isProjectPaused = computed(() => selectedProjectItem.value?.type === 'paused');
const isMarketPaused = computed(() => selectedMarketItem.value?.type === 'paused');

const authStore = useAuthStore();
const { super_admin } = storeToRefs(authStore);

const profile = useProfileStore();
const { user_name, user_image, activity_data } = storeToRefs(profile);

const subNavigationStore = useSubNavigationStore();
const { visibilityOptions, visibilityOptionsDashboard, visibilityOptionsVisibility, selectedVisibility, aiTypeOptionsDashboard, selectedAiTypeDashboard, aiTypeOptionsVisibility, selectedAiTypeVisibility } = storeToRefs(subNavigationStore);

const AiBrandMonitorStore = useAiBrandMonitorStore();
const { dates } = storeToRefs(AiBrandMonitorStore);

const KeywordsStore = useKeywordsStore();

const route = useRoute();
const router = useRouter();

const visibilityOptionsForRoute = computed(() => {
	return route.name === 'visibility'
		? visibilityOptionsVisibility.value
		: visibilityOptionsDashboard.value;
});

const aiTypeOptionsDashboardFiltered = computed(() => {
	const options = aiTypeOptionsDashboard.value || [];
	return perplexity_enabled.value ? options : options.filter(opt => opt.id !== 'perplexity');
});

const aiTypeOptionsVisibilityFiltered = computed(() => {
	const options = aiTypeOptionsVisibility.value || [];
	return perplexity_enabled.value ? options : options.filter(opt => opt.id !== 'perplexity');
});

const aiTypeIconMapDashboard = computed(() => ({
	global: robotIcon,
	ai_mode: aiOverviewIcon,
	chatgpt: chatgptIcon,
	perplexity: perplexityIconUrl,
	ai_overview: aiOverviewIcon,
}));

const aiTypeIconMapVisibility = computed(() => ({
	chatgpt: chatgptIcon,
	ai_mode: aiOverviewIcon,
	perplexity: perplexityIconUrl,
	ai_overview: aiOverviewIcon,
}));

const filteredProjects = computed(() => {
	if (super_admin.value.hide_paused_market) {
		return projects.value.filter(project => project.type !== 'paused');
	}
	return projects.value;
});

const filteredMarkets = computed(() => {
	if (super_admin.value.hide_paused_market) {
		return markets.value.filter(market => market.type !== 'paused');
	}
	return markets.value;
});

function getProjectTag(project) {
	if (!project || !project.type) return null;

	const tagConfig = {
		client: { label: 'Client', bgColor: '#faeed1', color: '#cd6d00' },
		paused: { label: 'Paused', bgColor: '#f3c8d3', color: '#890221' },
		pitch: { label: 'Pitch', bgColor: '#caf1e5', color: '#025138' },
	};

	return tagConfig[project.type] || null;
}

const dashboard_dates = JSON.parse(localStorage.getItem('dashboardSelectedDates'));
const visibility_dates = JSON.parse(localStorage.getItem('visibilitySelectedDates'));
const featureName = ref('');
const perplexityIconUrl = `${perplexityIcon}?v=1`;
const appCacheVersion = '2024-11-13-1';

const resetUserCacheIfNeeded = async () => {
	const cacheKey = 'app_cache_version';
	const previousVersion = localStorage.getItem(cacheKey);

	if (previousVersion === appCacheVersion) {
		return;
	}

	localStorage.setItem(cacheKey, appCacheVersion);

	if ('caches' in window) {
		try {
			const cacheNames = await caches.keys();
			await Promise.all(cacheNames.map(name => caches.delete(name)));
		} catch (error) {
			console.error('Failed to clear cache storage:', error);
		}
	}

	if ('serviceWorker' in navigator) {
		try {
			const registrations = await navigator.serviceWorker.getRegistrations();
			await Promise.all(registrations.map(registration => registration.unregister()));
		} catch (error) {
			console.error('Failed to unregister service workers:', error);
		}
	}

	window.location.reload();
};

window.dataLayer = window.dataLayer || [];
async function changeProject(id) {
	const projectToSwitch = getItemById(projects.value, id);
	const isPausedProject = projectToSwitch?.type === 'paused';

	marketStore.selectProject(id);
	KeywordsStore.setLoading(true);

	if (isPausedProject) {
		window.location.href = '/';
		return;
	}

	if (route?.meta?.onMarketChange) {
		redirectRoute.value = route?.meta?.onMarketChange;
		await router.push({ name: route?.meta?.onMarketChange });
	}

	location.reload();
}

async function changeMarket(id) {
	const marketToSwitch = getItemById(markets.value, id);
	const isPausedMarket = marketToSwitch?.type === 'paused';

	if (isPausedMarket) {
		marketStore.selectMarket(id);
		await router.push('/');
		return;
	}

	if (route?.meta?.onMarketChange) {
		redirectRoute.value = route?.meta?.onMarketChange;
		await router.push({ name: route?.meta?.onMarketChange });
	}

	marketStore.selectMarket(id);
	KeywordsStore.setLoading(true);

	if (!route?.meta?.onMarketChange) {
		location.reload();
	}
}

if (typeof selectedProject.value == 'object') {
	let key1 = '_vts' in selectedProject.value;
	let key2 = 'isTrusted' in selectedProject.value;

	if (key1 || key2) {
		selectedProject.value = 'na';
		location.reload();
	}
}

if (typeof selectedMarket.value == 'object' && selectedMarket.value != null) {
	let key1 = '_vts' in selectedProject.value;
	let key2 = 'isTrusted' in selectedProject.value;

	if (key1 || key2) {
		selectedProject.value = null;
		location.reload();
	}
}

onMounted(async () => {
	const twoMinutesAgo = new Date().getTime() - 0.5 * 1000;
	if (marketStore.force_key < twoMinutesAgo) {
		try {
			await marketStore.fetchData();
		} catch (error) {
			console.error('Failed to fetch market data:', error);
		}
	}
	await resetUserCacheIfNeeded();
});

onMounted(async () => {
	if (!authStore.isLoggedIn) {
		marketStore.selectedMarket = null;
		localStorage.removeItem('kpi');
		localStorage.removeItem('profile');
		localStorage.removeItem('market');
		localStorage.removeItem('auth');
	}
});

watch([selectedProject, selectedMarket], ([newSelectedProject, newSelectedMarket]) => {
	const projectItem = getItemById(projects.value, newSelectedProject);
	const marketItem = getItemById(markets.value, newSelectedMarket);

	if (newSelectedMarket) {
		KeywordsStore.fetchCompetitors(newSelectedMarket);
	}

	jam.metadata(() => {
		return {
			user: user_name ? { id: activity_data.value.user_id, name: user_name.value, image: user_image.value } : null,
			selectedProject: projectItem ? { id: projectItem.id, name: projectItem.name } : null,
			selectedMarket: marketItem ? { id: marketItem.id, name: marketItem.name } : null,
			dashboard_dates: dashboard_dates ? { from: dashboard_dates['from'], to: dashboard_dates['to'] } : null,
			visibility_dates: visibility_dates ? { from: visibility_dates['from'], to: visibility_dates['to'] } : null,
		};
	});
}, { immediate: true });

watch(route, () => {
	const dashboardRoutes = ['dashboard', 'team-management', 'geo-analysis'];

	if (dashboardRoutes.includes(route.name)) {
		featureName.value = 'dashboard geo';
	} else {
		featureName.value = 'people also ask';
	}
}, { immediate: true, deep: true });

watch([route, redirectRoute], ([newRoute, newRedirectRoute]) => {
	if (newRedirectRoute && newRoute.name === newRedirectRoute) {
		marketStore.redirectRoute = null;
		location.reload();
	}
});

watch([isProjectPaused, isMarketPaused], ([isProjectPausedVal, isMarketPausedVal]) => {
	if (isProjectPausedVal || isMarketPausedVal) {
		const dashboardRoutes = ['dashboard', 'seo-analysis', 'geo-analysis', 'team-management'];
		if (!dashboardRoutes.includes(route.name)) {
			router.push('/');
		}
	}
});

watch([selectedAiTypeDashboard, aiTypeOptionsDashboard], ([selectedAiType, aiTypeOptions]) => {
	const allowedIds = new Set((aiTypeOptions || []).map(option => option.id));
	if (!allowedIds.has(selectedAiType)) {
		selectedAiTypeDashboard.value = 'global';
	}
}, { immediate: true, deep: true });

watch([perplexity_enabled, selectedAiTypeDashboard, selectedAiTypeVisibility], ([enabled, dashboardType, visibilityType]) => {
	if (!enabled) {
		if (dashboardType === 'perplexity') {
			selectedAiTypeDashboard.value = 'global';
		}
		if (visibilityType === 'perplexity') {
			selectedAiTypeVisibility.value = 'chatgpt';
		}
	}
}, { immediate: true });
</script>

<style lang="scss" scoped>
header {
	position: sticky;
	top: 0;
	z-index: 100;
	background-color: var(--beige-100);
	height: 105px;
	transition: all 0.3s ease;
	will-change: height, transform;
	width: 100%;
}

header.shrink {
	height: 50px;
	padding: 2rem 0;
	width: 100%;
	transform: scaleY(.95) translateY(-3.5px);
}
</style>
